import { HttpErrorResponse } from '@angular/common/http';

export interface ApiErrorBody {
  message?: string | string[];
  error?: string;
}

export type ApiErrorBodyLike = ApiErrorBody | string | null | undefined;

const FIELD_LABEL_MAX_LENGTH = 128;
const REPEATED_VALIDATION_MARKER = ' Please enter';

function isAsciiLetter(code: number): boolean {
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

function isFieldLabelChar(code: number): boolean {
  return isAsciiLetter(code) || code === 95 || (code >= 48 && code <= 57);
}

/** Validates `notification_product_purchased_emails`-style field tokens without regex. */
function isFieldLabelToken(fieldName: string): boolean {
  if (fieldName.length === 0 || fieldName.length > FIELD_LABEL_MAX_LENGTH) {
    return false;
  }
  const first = fieldName.codePointAt(0);
  if (first === undefined || !isAsciiLetter(first)) {
    return false;
  }
  for (let i = 1; i < fieldName.length; i++) {
    const code = fieldName.codePointAt(i);
    if (code === undefined || !isFieldLabelChar(code)) {
      return false;
    }
  }
  return true;
}

/** Splits repeated validation sentences within one API message entry. */
function splitRepeatedValidationSentences(body: string): string[] {
  const parts: string[] = [];
  let segmentStart = 0;
  let searchFrom = 0;

  while (searchFrom < body.length) {
    const markerIndex = body.indexOf(REPEATED_VALIDATION_MARKER, searchFrom);
    if (markerIndex === -1) {
      break;
    }

    if (markerIndex > 0 && body[markerIndex - 1] === '?') {
      const segment = body.slice(segmentStart, markerIndex + 1).trim();
      if (segment.length > 0) {
        parts.push(segment);
      }
      segmentStart = markerIndex + 1;
      while (segmentStart < body.length && body[segmentStart] === ' ') {
        segmentStart++;
      }
      searchFrom = segmentStart;
      continue;
    }

    searchFrom = markerIndex + 1;
  }

  const tail = body.slice(segmentStart).trim();
  if (tail.length > 0) {
    parts.push(tail);
  }

  return parts.length > 0 ? parts : [body.trim()].filter((part) => part.length > 0);
}

/** Turns `notification_product_purchased_emails` into a readable field label. */
function humanizeNotificationField(field: string): string {
  return field
    .replace(/^notification_/i, '')
    .replace(/_emails$/i, ' notification emails')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Formats one API validation message for toast display. Field-prefixed messages
 * with multiple repeated sentences are broken into a labeled group with bullets.
 */
export function formatValidationMessageEntry(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return trimmed;

  // Split on the first colon with plain string ops (no backtracking-prone regex): the prefix
  // must be a single token (letters/digits/underscore) to be treated as a field label.
  const colonIndex = trimmed.indexOf(':');
  const fieldName = colonIndex > 0 ? trimmed.slice(0, colonIndex).trim() : '';
  const body = colonIndex > 0 ? trimmed.slice(colonIndex + 1).trim() : '';
  if (body.length === 0 || !isFieldLabelToken(fieldName)) return trimmed;

  const fieldLabel = humanizeNotificationField(fieldName);
  const subErrors = splitRepeatedValidationSentences(body);

  if (subErrors.length <= 1) {
    return `${fieldLabel}:\n• ${body}`;
  }

  return `${fieldLabel}:\n• ${subErrors.join('\n• ')}`;
}

export function messageFromApiErrorBody(
  body: ApiErrorBodyLike,
  options: { includeErrorField?: boolean } = {},
): string | null {
  if (typeof body === 'string') {
    const t = body.trim();
    return t.length > 0 ? t : null;
  }
  if (!body || typeof body !== 'object') return null;

  const { message } = body;
  if (typeof message === 'string' && message.length > 0) {
    return formatValidationMessageEntry(message);
  }
  if (Array.isArray(message) && message.length > 0) {
    const parts = message
      .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
      .map(formatValidationMessageEntry);
    if (parts.length > 0) return parts.join('\n\n');
  }
  if (
    options.includeErrorField !== false &&
    typeof body.error === 'string' &&
    body.error.length > 0
  ) {
    return body.error;
  }
  return null;
}

export function extractApiErrorMessage(
  err: unknown,
  fallback = 'An error occurred. Please try again.',
): string {
  if (err instanceof HttpErrorResponse) {
    return messageFromApiErrorBody(err.error as ApiErrorBodyLike) ?? (err.message || fallback);
  }
  if (err instanceof Error && err.message.length > 0) return err.message;
  return fallback;
}
